import React, { Component } from "react";
import { connect } from "react-redux";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { goBack, push } from "react-router-redux";

import { selectors, actions } from "reducers/administration/common/administrationTicket";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";

import * as i18nUtils from "util/i18n";
import { exportGenericTicketPdfWithSuccessMessage, getGeneralTicketItem } from "util/transaction";
import { parseFloatToAmountFormat } from "util/number";
import { getAccountDescription } from "util/accounts";

import Heading from "pages/_components/Heading";
import FormattedDate from "pages/_components/FormattedDate";
import TransactionTicket from "pages/_components/TransactionTicket";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

class CurrencyExchangeTicket extends Component {
    static propTypes = {
        isDesktop: bool.isRequired,
        isSmallDesktop: bool.isRequired,
        transactionData: shape({}).isRequired,
        dispatch: func.isRequired,
        match: shape({
            url: string.isRequired,
            params: shape({ idTransaction: string.isRequired }),
        }).isRequired,
        fetching: bool.isRequired,
        fetchingAccounts: bool,
        accounts: arrayOf(shape({})),
        userId: string,
        embeddedInV3Shell: bool,
    };

    static defaultProps = {
        fetchingAccounts: false,
        accounts: [],
        userId: "",
        embeddedInV3Shell: false,
    };

    state = {
        fetchingDownload: false,
    };

    componentDidMount() {
        const { match, dispatch, accounts, userId } = this.props;
        const { idTransaction } = match.params;
        dispatch(actions.loadAdministrationTicketRequest(idTransaction));
        if (accounts.length === 0 && userId) {
            dispatch(accountsActions.listAccounts(userId));
        }
        this.syncEmbeddedV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { embeddedInV3Shell, isDesktop, transactionData } = this.props;
        if (
            prevProps.embeddedInV3Shell !== embeddedInV3Shell ||
            prevProps.isDesktop !== isDesktop ||
            prevProps.transactionData !== transactionData
        ) {
            this.syncEmbeddedV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { dispatch, embeddedInV3Shell } = this.props;
        if (embeddedInV3Shell) {
            clearEmbeddedV3ShellLayoutState(dispatch);
        }
    }

    syncEmbeddedV3ShellLayout = () => {
        const { dispatch, embeddedInV3Shell, isDesktop, transactionData } = this.props;
        if (!transactionData?.idTransaction) {
            return;
        }
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            headerTitle: this.getTitle(),
            goBackPath: "/v2/investment/currencyExchange",
            scopes: ["currencyExchangeConfirmation"],
        });
    };

    getTitle = () => {
        const { transactionData } = this.props;
        const isBuy = transactionData.idActivity === "currencyExchange.confirm.buy";
        return i18nUtils.get(isBuy ? "currencyExchange.ui.ticketTitleBuy" : "currencyExchange.ui.ticketTitleSell");
    };

    getFormattedAccount = (accountNumber) => {
        const { accounts } = this.props;
        const account = accounts.find((acc) => acc.number === accountNumber);
        if (!account) {
            return accountNumber;
        }
        return getAccountDescription(account);
    };

    formatAmountWithCurrency = (amount, currency) => {
        const currencyLabel = i18nUtils.get(`currency.label.${currency}`);
        return `${currencyLabel} ${parseFloatToAmountFormat(amount)}`;
    };

    centerContentMobile = () => (
        <div className="title-account-header-multiline-background-blue">
            <h1 style={{ margin: "-0.5rem 2rem" }}>{this.getTitle()}</h1>
        </div>
    );

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(goBack());
    };

    exportPdf = async () => {
        this.setState({ fetchingDownload: true });
        const { dispatch } = this.props;

        const ticketData = {
            ticketName: this.getTitle(),
            ticketBody: [...this.generateTicketBody()],
        };
        await exportGenericTicketPdfWithSuccessMessage(ticketData, dispatch, i18nUtils.get("global.download.success"));

        this.setState({ fetchingDownload: false });
    };

    generateTicketBody = () => {
        const {
            transactionData,
            transactionData: { data },
        } = this.props;

        const isBuy = transactionData.idActivity === "currencyExchange.confirm.buy";
        const debitCurrency = isBuy ? "ARS" : "USD";
        const creditCurrency = isBuy ? "USD" : "ARS";

        return [
            getGeneralTicketItem(
                "currencyExchange.ui.ticketDateTime",
                transactionData?.creationDateTime?.toString().replace("Z", ""),
                "datefulltime",
            ),
            getGeneralTicketItem(
                "currencyExchange.ui.ticketVoucher",
                transactionData?.idTransaction?.substring(0, 8).toUpperCase(),
                "string",
            ),
            getGeneralTicketItem(
                "currencyExchange.ui.ticketOperationNumber",
                data?.solicitudCambioId?.toString(),
                "string",
            ),
            getGeneralTicketItem(
                "currencyExchange.ui.ticketDebitAccount",
                this.getFormattedAccount(data?.cuentaDebito),
                "string",
            ),
            getGeneralTicketItem(
                "currencyExchange.ui.ticketCreditAccount",
                this.getFormattedAccount(data?.cuentaCredito),
                "string",
            ),
            getGeneralTicketItem(
                "currencyExchange.ui.debitAmount",
                this.formatAmountWithCurrency(data?.importeDebito, debitCurrency),
                "string",
            ),
            getGeneralTicketItem(
                "currencyExchange.ui.creditAmount",
                this.formatAmountWithCurrency(data?.importeCredito, creditCurrency),
                "string",
            ),
            getGeneralTicketItem(
                "currencyExchange.ticket.exchangeRate",
                this.formatAmountWithCurrency(data?.cotizacion, "ARS"),
                "string",
            ),
        ];
    };

    buttonsDetail = () => {
        const { dispatch, embeddedInV3Shell } = this.props;

        return [
            {
                label: "currencyExchange.ticket.goDesktop.button",
                bsStyle: "outline",
                onClick: () => dispatch(push(embeddedInV3Shell ? "/v3/dashboard" : "/desktop")),
            },
            {
                label: "currencyExchange.ticket.goToCurrencyExchange.button",
                bsStyle: "primary",
                onClick: () => dispatch(push("/v2/investment/currencyExchange")),
            },
        ];
    };

    render() {
        const {
            transactionData,
            transactionData: { data },
            isDesktop,
            isSmallDesktop,
            fetching,
            fetchingAccounts,
            embeddedInV3Shell,
        } = this.props;

        const { fetchingDownload } = this.state;

        const creatorFullName =
            transactionData?.creatorFullName ||
            `${transactionData.userCreatorLastName}, ${transactionData.userCreatorFirstName}`;

        const isBuy = transactionData.idActivity === "currencyExchange.confirm.buy";
        const containerClassName = "transaction-ticket-detail data-wrapper-flex space-between";

        return (
            <>
                {data && (
                    <TransactionTicket
                        notificationScope="currencyExchangeConfirmation"
                        embeddedInV3Shell={embeddedInV3Shell}
                        embeddedV3GoBackPath="/v2/investment/currencyExchange"
                        embeddedV3LayoutScopes={["currencyExchangeConfirmation"]}
                        handleClick={this.exportPdf}
                        defaultConfiguration
                        headerClassName={!isDesktop ? "blue-main-header-mobile blue-main-title-mobile" : ""}
                        centerElement={isDesktop ? undefined : this.centerContentMobile}
                        hasInlineButtons
                        hideMobileMenu={!isDesktop}
                        handleBack={this.handleBack}
                        summary={{ ...transactionData, creatorFullName }}
                        buttonsDetail={this.buttonsDetail()}
                        isDesktop={isDesktop}
                        isSmallDesktop={isSmallDesktop}
                        fetching={fetching || fetchingAccounts}
                        summaryTitle={
                            isBuy ? "currencyExchange.ui.ticketTitleBuy" : "currencyExchange.ui.ticketTitleSell"
                        }
                        generateSignFunction
                        ignoreHomeButton
                        isFetchingExport={fetchingDownload}>
                        <div>
                            {transactionData.idTransactionStatus === "FINISHED" && (
                                <Heading.DataGroup
                                    containerClassName={containerClassName}
                                    labelClassName="data-label-medium"
                                    dataClassName="data-desc"
                                    label="currencyExchange.ui.ticketDateTime"
                                    data={
                                        <FormattedDate
                                            date={transactionData.creationDateTime?.toString().replace("Z", "")}
                                            dateFormat="dd/MM/yyyy"
                                            showTime
                                            small
                                        />
                                    }
                                />
                            )}
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ui.ticketVoucher"
                                data={
                                    <p className="data-desc m-0">
                                        {transactionData.idTransaction?.substring(0, 8).toUpperCase()}
                                    </p>
                                }
                            />
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ui.ticketOperationNumber"
                                data={<p className="data-desc m-0">{data.solicitudCambioId}</p>}
                            />
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ui.ticketDebitAccount"
                                data={<p className="data-desc m-0">{this.getFormattedAccount(data.cuentaDebito)}</p>}
                            />
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ui.ticketCreditAccount"
                                data={<p className="data-desc m-0">{this.getFormattedAccount(data.cuentaCredito)}</p>}
                            />
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ui.debitAmount"
                                data={
                                    <p className="data-desc m-0">
                                        {this.formatAmountWithCurrency(data.importeDebito, isBuy ? "ARS" : "USD")}
                                    </p>
                                }
                            />
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ui.creditAmount"
                                data={
                                    <p className="data-desc m-0">
                                        {this.formatAmountWithCurrency(data.importeCredito, isBuy ? "USD" : "ARS")}
                                    </p>
                                }
                            />
                            <Heading.DataGroup
                                containerClassName={containerClassName}
                                labelClassName="data-label-medium"
                                dataClassName="data-desc"
                                label="currencyExchange.ticket.exchangeRate"
                                data={
                                    <p className="data-desc m-0">
                                        {this.formatAmountWithCurrency(data.cotizacion, "ARS")}
                                    </p>
                                }
                            />
                            {transactionData.idTransactionStatus === "FAILED" && (
                                <>
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName="data-label-medium"
                                        dataClassName="data-desc"
                                        label="failedVoucher.error.code.label"
                                        data={<p className="data-desc m-0">{data.errorCode}</p>}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName="data-label-medium"
                                        dataClassName="data-desc"
                                        label="failedVoucher.error.message.label"
                                        data={<p className="data-desc m-0">{data.errorDescription}</p>}
                                    />
                                </>
                            )}
                        </div>
                    </TransactionTicket>
                )}
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    transactionData: selectors.getData(state),
    fetching: selectors.isFetching(state),
    fetchingAccounts: accountsSelectors.getFetching(state),
    accounts: accountsSelectors.getAccounts(state),
    userId: sessionSelectors.getUser(state)?.userId || "",
});

export default connect(mapStateToProps)(resizableRoute(CurrencyExchangeTicket));
